import { useState } from 'react';
import { Grid, Typography, MenuItem } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomSelect,
  CustomTextField,
  CustomCheckbox,
} from '../../../common';
import { useRecLegalEntities } from '../../../hooks/useLegalEntities';
import { useSalePoints } from '../../../hooks/useSalePoints';
import { getCurrentDate } from '../utils/externalPaymentUtils';

const CouponsVerificationDialog = ({ open, onClose, coupons, lastLineData, onConfirm }) => {
  const { data: recLegalEntities = [] } = useRecLegalEntities();
  const { data: salePoints = [] } = useSalePoints();

  const [entidadRecaudadora, setEntidadRecaudadora] = useState('');
  const [puntoDeVenta, setPuntoDeVenta] = useState('');
  const [electronicInvoice, setElectronicInvoice] = useState(false);
  const [fechaFactura, setFechaFactura] = useState(getCurrentDate());

  const resetForm = () => {
    setEntidadRecaudadora('');
    setPuntoDeVenta('');
    setElectronicInvoice(false);
    setFechaFactura(getCurrentDate());
  };

  const handleConfirm = () => {
    onConfirm(entidadRecaudadora, puntoDeVenta, electronicInvoice, fechaFactura);
    resetForm();
    onClose();
  };

  const headerCellStyle = {
    fontWeight: 'bold',
    backgroundColor: '#ccc',
    textAlign: 'center',
    padding: '8px',
  };

  const greyCellStyle = {
    backgroundColor: '#f2f2f2',
    border: '1px solid #a6a4a4',
    padding: '5px',
  };

  const sectionStyle = {
    marginBottom: '16px',
  };

  const renderCouponDueDate = (coupon, dueType, amount, date) => {
    const paymentDate = new Date(coupon.paymentDate.split('/').reverse().join('-'));
    const dueDate = date ? new Date(date) : null;

    let isHighlighted = false;

    if (dueType === 'first' && dueDate && paymentDate <= dueDate) {
      isHighlighted = true;
    } else if (dueType === 'second' && dueDate && coupon.firstDueDate) {
      const firstDue = new Date(coupon.firstDueDate);
      isHighlighted = paymentDate > firstDue && paymentDate <= dueDate;
    } else if (dueType === 'third' && dueDate && coupon.secondDueDate) {
      const secondDue = new Date(coupon.secondDueDate);
      isHighlighted = paymentDate > secondDue && paymentDate <= dueDate;
    }

    return (
      <Typography style={isHighlighted ? greyCellStyle : null} sx={{ padding: '5px' }}>
        Importe {dueType === 'first' ? '1er' : dueType === 'second' ? '2do' : '3er'} Venc: ${amount}
        , Fecha {dueType === 'first' ? '1er' : dueType === 'second' ? '2do' : '3er'} Venc: {date}
      </Typography>
    );
  };

  return (
    <DialogContainer open={open} onClose={onClose} size="large">
      <DialogTitle>
        Cupones a procesar: {lastLineData.cantCupones}{' '}
        <span style={{ fontWeight: 'normal' }}>(Archivo: {lastLineData.fileName})</span>
      </DialogTitle>

      <DialogBody>
        <Grid container spacing={2} style={sectionStyle}>
          <Grid item xs={6} style={headerCellStyle}>
            Archivo txt: ${lastLineData.importeTotalTxt}
          </Grid>
          <Grid item xs={6} style={headerCellStyle}>
            Cupones Internos: ${lastLineData.importeTotalDb}
          </Grid>
        </Grid>

        {coupons.map((coupon, index) => (
          <Grid container key={index} spacing={2} style={sectionStyle}>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 'bold' }}>
                Num.Orden: {parseInt(coupon.orderNumber, 10).toString()}
              </Typography>
              <Typography>Fecha de pago: {coupon.paymentDate}</Typography>
              <Typography>Importe: ${coupon.amount}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 'bold' }}>
                Mes: {coupon.month} - {coupon.year}, Cuota/s: {coupon.feeNumber}
              </Typography>

              {renderCouponDueDate(coupon, 'first', coupon.firstDueAmount, coupon.firstDueDate)}
              {renderCouponDueDate(coupon, 'second', coupon.secondDueAmount, coupon.secondDueDate)}
              {renderCouponDueDate(coupon, 'third', coupon.thirdDueAmount, coupon.thirdDueDate)}
            </Grid>
          </Grid>
        ))}

        <Grid container spacing={2} style={sectionStyle}>
          <Grid item xs={12} md={3}>
            <CustomSelect
              label="Entidad Recaudadora"
              value={entidadRecaudadora}
              onChange={(e) => setEntidadRecaudadora(e.target.value)}
              size="medium"
            >
              {recLegalEntities.map((option) => (
                <MenuItem key={option.uuid} value={option.uuid}>
                  {option.legal_business_name}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>

          <Grid item xs={12} md={3}>
            <CustomSelect
              label="Punto de venta"
              value={puntoDeVenta}
              onChange={(e) => setPuntoDeVenta(e.target.value)}
              size="medium"
            >
              {salePoints.map((option) => (
                <MenuItem key={option.uuid} value={option.uuid}>
                  {option.number}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>

          <Grid item xs={12} md={3}>
            <CustomTextField
              label="Fecha factura"
              type="date"
              value={fechaFactura}
              onChange={(e) => setFechaFactura(e.target.value)}
              size="medium"
              shrinkLabel={true}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <CustomCheckbox
              checked={electronicInvoice}
              onChange={(e) => setElectronicInvoice(e.target.checked)}
              label="No generar CAE"
            />
          </Grid>
        </Grid>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleConfirm}
      />
    </DialogContainer>
  );
};

export default CouponsVerificationDialog;
